import { customTextSchema } from "features/CenterContent/RoleContent/GlobalEvents/types/responseSchema";
import { WeekRepeat } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/durationInputs/PeriodicDuration";
import { baseSchema } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/baseSchema";
import {
  describedTypeSchema,
  stopSchema,
} from "features/CenterContent/shared/operationalInformation/utils";
import { dateTimeSchemaForm } from "shared/form/dateTimeSchema";
import { z } from "zod";

const openEndedDurationSchema = z.object({
  type: z.literal("OPEN_ENDED"),
  from_time: dateTimeSchemaForm,
  extension_time_in_minutes: z.string().refine((val) => Number(val) > 0, {
    message: "Tidshorisont må være et positivt tall",
  }),
});

export const timedDurationSchema = z
  .object({
    type: z.literal("TIMED"),
    from_time: dateTimeSchemaForm,
    to_time: dateTimeSchemaForm,
  })
  // This refine unfortunately forces discriminatedUnion -> union. But it has to be done for proper validation
  .refine((data) => data.to_time >= data.from_time, {
    message: "Sluttidspunkt må være etter starttidspunkt",
    path: ["to_time"],
  });

const ruleSchema = z.object({
  id: z.string(),
  startDateTime: dateTimeSchemaForm,
  endDateTime: dateTimeSchemaForm,
  days: z.array(z.number()),
  repeat: z.nativeEnum(WeekRepeat),
});

const periodicDurationSchema = z.object({
  type: z.literal("PERIODIC"),
  rules: z
    .array(ruleSchema)
    .min(1)
    .refine(
      (data) => data.some((rule) => rule.endDateTime >= rule.startDateTime),
      { message: "Sluttidspunkt må være etter starttidspunkt" },
    ),
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Is used to construct the type `FormSchemaDuration`
const durationSchema = z.union([
  openEndedDurationSchema,
  timedDurationSchema,
  periodicDurationSchema,
]);

export type FormSchemaDuration = z.infer<typeof durationSchema>;

/**
 * Limits the duration to 1440 minutes in order to prevent "self DDOS-ing"
 */
const superRefineLimitDuration = (
  data: FormSchemaDuration,
  ctx: z.RefinementCtx,
) => {
  const limitMinutes = 1440;
  const message = "Tidshorisont kan ikke overstige 1440 minutter (1 døgn)";
  switch (data.type) {
    case "PERIODIC":
      break;
    case "OPEN_ENDED": {
      if (Number(data.extension_time_in_minutes) > limitMinutes) {
        ctx.addIssue({
          code: "custom",
          message,
          path: ["extension_time_in_minutes"],
        });
      }
      break;
    }
    case "TIMED": {
      // We need to ask Eskild if they want to limit the duration for "TIMED" durations as well
      /*
      const limitedMilliSeconds = limitMinutes * 60 * 1000;
      const toTime = calendarDateTimeToDate(data.to_time);
      const fromTime = calendarDateTimeToDate(data.from_time);
      const diffMilliSeconds = toTime.getTime() - fromTime.getTime();
      if (diffMilliSeconds > limitedMilliSeconds) {
        ctx.addIssue({ code: "custom", message, path: ["to_time"] });
      }
      */
      break;
    }
  }
};

const legSchema = z.object({
  fromStop: stopSchema,
  toStop: stopSchema,
});

const infrastructureCustomSchema = z.object({
  type: z.enum(["INFRASTRUCTURE_GENERAL"]),
  severity: z.enum(["INFO", "WARNING"], {
    errorMap: () => ({ message: "Du må velge en alvorlighetsgrad" }),
  }),
  affectedLegs: legSchema.array().min(1),
  distributions: customTextSchema,
  durations: z
    .union([openEndedDurationSchema, timedDurationSchema])
    .superRefine(superRefineLimitDuration),
  prognosisType: describedTypeSchema.nullable(),
  prognosisTime: dateTimeSchemaForm,
});

const plannedMaintenanceBaseSchema = z.object({
  type: z.enum(["INFRASTRUCTURE_PLANNED_MAINTENANCE"]),
  durations: z.union([timedDurationSchema, periodicDurationSchema]),
  affectedLegs: legSchema.array().min(1),
  isirkId: z.string().nullable(),
});

export const commonTypes = [
  "INFRASTRUCTURE_STRETCH_CLOSED",
  "INFRASTRUCTURE_STRETCH_PARTIALLY_CLOSED",
  "INFRASTRUCTURE_SLOW_DRIVING",
] as const;

const redirectStretchSchema = z.object({
  name: z.string(),
  legs: legSchema.array().min(1),
});

export type RedirectStretchSchema = z.infer<typeof redirectStretchSchema>;

const infrastructureCommonSchema = z.object({
  type: z.enum(commonTypes),
  actionCard: z.object({ type: z.string().nullable() }).nullable(),
  redirectStretches: z.array(redirectStretchSchema).max(1),
  affectedLegs: legSchema.array().min(1),
  durations: z
    .union([openEndedDurationSchema, timedDurationSchema])
    .superRefine(superRefineLimitDuration),
  prognosisType: describedTypeSchema.nullable(),
  prognosisTime: dateTimeSchemaForm,
});

export const infrastructureParametersSchema = baseSchema.and(
  z.discriminatedUnion(
    "type",
    [
      infrastructureCustomSchema,
      infrastructureCommonSchema,
      plannedMaintenanceBaseSchema,
    ],
    {
      errorMap: () => ({
        message: "Du må velge en hendelsestype for å kunne opprette hendelsen",
      }),
    },
  ),
);

export const formSchema = z.object({
  infrastructureForm: infrastructureParametersSchema,
});

export type InfrastructureInformationType = z.infer<
  typeof infrastructureParametersSchema
>["type"];
export type InfrastructureCommon = z.infer<typeof infrastructureCommonSchema>;
export type InfrastructureForm = z.infer<typeof formSchema>;
export type Rule = z.infer<typeof ruleSchema>;
export type OpenEndedFormDuration = z.infer<typeof openEndedDurationSchema>;
export type TimedFormDuration = z.infer<typeof timedDurationSchema>;
export type PeriodicFormDuration = z.infer<typeof periodicDurationSchema>;
